/**
 * ENG-4833 — PostHog contract for "how was this camera identified?".
 *
 * Wire values and property names only. The resolver and the typed trackers
 * live in `analytics/domains/device-identification.ts`; this file has NO
 * imports so `types/properties.ts` can reference these types without creating
 * an import cycle (madge runs `--circular` over src in CI).
 *
 * Every key is snake_case, matching the canonical device convention
 * (CONVENTIONS.md §1). None of them is one of the six off-convention device
 * keys, so `assertCanonicalDeviceProps` (track.ts) lets them through.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one source of truth for
 * the wire spellings, shared by the types, the resolver and the receipts.
 */

/**
 * How the operator arrived at the camera model that was saved (ENG-4833,
 * verbatim from the ticket).
 *
 * - `detected`: discovery named the camera (or at least its brand) and the
 *   operator kept what discovery resolved.
 * - `brand_detected_then_searched`: discovery resolved only a brand; the
 *   operator then searched and picked the model themselves.
 * - `searched`: the operator found the model through brand/model search
 *   (including the case where discovery named a camera and the operator picked
 *   another — the ticket has no separate bucket for that).
 * - `not_yet_supported_picked`: the operator picked a model the registry marks
 *   not yet supported (no driver of its own yet).
 * - `not_listed`: the operator went through the "not listed" free-text step.
 *
 * Older builds persisted `detected_then_changed`, `not_listed_typed` and
 * `skipped` on StudioDevice; the resolver maps those to the values above.
 */
export const IDENTIFIED_BY_VALUES = [
  'detected',
  'brand_detected_then_searched',
  'searched',
  'not_yet_supported_picked',
  'not_listed'
] as const;

export type IdentifiedBy = (typeof IDENTIFIED_BY_VALUES)[number];

/**
 * Which control driver the saved camera ends up on (ENG-4833).
 *
 * - `own`: a curated registry model with its own driver.
 * - `generic`: a brand-generic / "Other" / not-yet-supported model, or no model.
 *
 * No Control does not change the driver type — it is resolved from the model
 * alone (the ticket has no `none` bucket).
 */
export const DRIVER_TYPE_VALUES = ['own', 'generic'] as const;

export type DriverType = (typeof DRIVER_TYPE_VALUES)[number];

/** Wire spellings of the six identification props (ENG-4833 design table). */
export const IDENTIFICATION_PROPERTIES = {
  IDENTIFIED_BY: 'identified_by',
  DETECTED_BRAND: 'detected_brand',
  DETECTED_MODEL: 'detected_model',
  TYPED_BRAND: 'typed_brand',
  TYPED_MODEL: 'typed_model',
  DRIVER_TYPE: 'driver_type'
} as const;

/**
 * The six props added to the camera-save success events (`device_add` and
 * `multi_add_device_added`). The strings are '' when absent rather than
 * omitted, so a PostHog breakdown buckets "nothing detected" instead of
 * silently dropping the row.
 */
export interface DeviceIdentificationProperties {
  identified_by: IdentifiedBy;
  detected_brand: string;
  detected_model: string;
  typed_brand: string;
  typed_model: string;
  driver_type: DriverType;
}

/** `device_search_no_matches.search_field` — which search box returned nothing (ENG-4831). */
export const SEARCH_NO_MATCHES_FIELD_VALUES = ['brand', 'model'] as const;

export type SearchNoMatchesField = (typeof SEARCH_NO_MATCHES_FIELD_VALUES)[number];

/**
 * `device_not_listed_closed.opened_from` — which entry point opened the "not
 * listed" pop-ups (ENG-4832): the brand/model list's trailing action, the
 * not-yet-supported pick, or a camera reporting itself.
 */
export const NOT_LISTED_OPENED_FROM_VALUES = [
  'list',
  'not_yet_supported_pick',
  'camera_reported'
] as const;

export type NotListedOpenedFrom = (typeof NOT_LISTED_OPENED_FROM_VALUES)[number];

/**
 * `device_not_listed_closed.outcome` — how the operator left the "not listed"
 * pop-up (ENG-4832): `continue` / `did_you_mean` are the two forward buttons,
 * `back` is the Back button, `closed` is Escape / outside click / header X.
 */
export const NOT_LISTED_OUTCOME_VALUES = ['continue', 'did_you_mean', 'back', 'closed'] as const;

export type NotListedOutcome = (typeof NOT_LISTED_OUTCOME_VALUES)[number];

/**
 * `device_basic_controls_closed.outcome` — how the basic-controls notice was closed (ENG-4925,
 * Board D): `continue` keeps the camera, `closed` (header X / Escape) returns to brand/model.
 */
export const BASIC_CONTROLS_OUTCOME_VALUES = ['continue', 'closed'] as const;

export type BasicControlsOutcome = (typeof BASIC_CONTROLS_OUTCOME_VALUES)[number];
